import "./MyHardwareOrdersTable.scss";

import { Order } from "@amzn/ito-client";
import { Col, Flex, Row, Spacer, View } from "@amzn/stencil-react-components/layout";
import { MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { RowAttributesFunction, Table, TableSpacing } from "@amzn/stencil-react-components/table";
import { buildColumnDefinitions } from "common/helpers/table-utils";
import { ITOConsumers } from "common/types";
import { ColumnSetting } from "common/types/config-model";
import { OrderItemInformation, OrderStatus } from "common/types/orders-types";
import { getString } from "common/uistringlabels/uiStringUtils";
import { PERMISSIONS } from "config";
import { UserPermissions } from "lib/services";
import React, { FC, useCallback, useEffect, useState } from "react";

import { useMessageBannersBox } from "../MessageBannersBox/hook";
import {
  CancellationReason,
  CancelOrderButton,
  mapToOrderStatus,
  OrderDetails,
  OrderItemWithHopDetails,
  useCancellation,
} from "../OrderCancellation";
import { extractOrderData } from "./components/OrderDataExtractor";
import { HardwareDetailsSection, OrderDetailsSection, ShippingDetailsSection } from "./components/OrderDetailsSection";
import myOrdersTableConfig from "./my-hardware-orders.table-config.json";
import { useOrdersTable } from "./useOrdersPageTable";

/** Auto-dismiss time for success banner in milliseconds */
const SUCCESS_BANNER_AUTO_DISMISS_MS = 5000;

/**
 * Extracts order details for the cancellation modal.
 * @param order - The ITO Order object
 * @returns OrderDetails for the cancellation modal
 */
const extractCancellationOrderDetails = (order: Order): OrderDetails => {
  const firstItem = order.items && order.items.length > 0 ? order.items[0] : undefined;
  const assetName = firstItem
    ? (firstItem.itemInformation as OrderItemInformation | undefined)?.name || "Unknown Asset"
    : "Unknown Asset";

  return {
    orderId: order.orderId || "",
    orderReference: order.orderReference || order.orderId || "",
    assetName,
    requestDate: order.requested?.on ? new Date(order.requested.on).toLocaleDateString() : "Unknown",
  };
};

export const MyHardwareOrdersTable: FC = () => {
  // Email Orders send the taxonomy as a hash
  const hash = window.location.hash.replace("#", "");

  const useHardwareOrderSupport = UserPermissions.current.hasPermission(PERMISSIONS.USE_SUPPORT_PHASE_2);

  const isCancellationEnabled = UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_HARDWARE_ORDER_CANCELLATION);

  const columns: ColumnSetting[] = myOrdersTableConfig.columns;

  const filteredColumns = columns.filter((column) => {
    if (column.id === "order_messages" && !useHardwareOrderSupport) {
      return false;
    }
    return true;
  });

  const currentColumnDefinitions = buildColumnDefinitions(filteredColumns);

  const { orders: fetchedOrders, isLoading } = useOrdersTable(ITOConsumers.AMZNHAM);

  const [orders, setOrders] = useState<Order[]>([]);

  useEffect(() => {
    if (fetchedOrders && fetchedOrders.length > 0) {
      setOrders(fetchedOrders);
    }
  }, [fetchedOrders]);

  const { state: cancellationState, cancelOrder } = useCancellation();
  const { addMessageBanner } = useMessageBannersBox();

  /* istanbul ignore next -- tested via buildExpandableContent + useCancellation unit tests */
  const handleConfirmCancellation = useCallback(
    async (orderId: string, reason: CancellationReason) => {
      const success = await cancelOrder(orderId, reason);

      if (success) {
        setOrders((prevOrders) =>
          prevOrders.map((order) => (order.orderId === orderId ? { ...order, status: OrderStatus.CANCELLED } : order))
        );

        addMessageBanner(
          getString("hardwareOrderCancellation.toast.message"),
          MessageBannerType.Success,
          SUCCESS_BANNER_AUTO_DISMISS_MS
        );
      }
    },
    [cancelOrder, addMessageBanner]
  );

  /* istanbul ignore next */
  const expandableContent = ({ data, index }: { data: Order; index: number }) => {
    return buildExpandableContent(
      data,
      index,
      cancellationState,
      isCancellationEnabled ? handleConfirmCancellation : undefined
    );
  };

  /* eslint-disable @typescript-eslint/no-unused-vars */
  const getRowAttributes: RowAttributesFunction = ({ data }) => ({
    expandableContent,
    disabled: false,
    id: data.orderId,
    isDefaultExpanded: data.orderId === hash,
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Col gridGap={32}>
      <Table
        actionHeader="Detail"
        columns={currentColumnDefinitions}
        data={orders}
        spacing={TableSpacing.Default}
        getRowAttributes={getRowAttributes}
      />
    </Col>
  );
};

const LoadingSpinner: FC = () => (
  <Flex flexDirection="column">
    <View alignSelf="center">
      <Spacer height={32} width={32} />
      <Spinner size={SpinnerSize.Medium} showText loadingText="Loading your orders" />
    </View>
  </Flex>
);

export const buildExpandableContent = (
  order: Order,
  index: number,
  cancellationState: { isLoading: boolean; error: string | null },
  onConfirmCancellation?: (orderId: string, reason: CancellationReason) => void
): React.JSX.Element[] => {
  const innerContent: React.JSX.Element[] = [];
  const orderData = extractOrderData(order);
  const orderStatus = mapToOrderStatus(order.status);

  innerContent.push(
    <Row key={`detailsRow_${index}`} gridGap="S400" style={{ width: "100%" }}>
      <OrderDetailsSection index={index} orderData={orderData} />
      <div style={{ width: "1px", backgroundColor: "#e0e0e0", margin: "0 8px" }} />
      <ShippingDetailsSection index={index} orderData={orderData} />
    </Row>
  );

  if (order.status === "DELIVERED") {
    innerContent.push(
      <Row key={`hardwareRow_${index}`} gridGap="S400" style={{ width: "100%", paddingTop: "8px" }}>
        <HardwareDetailsSection index={index} orderData={orderData} />
      </Row>
    );
  }

  if (onConfirmCancellation && orderStatus !== OrderStatus.CANCELLED) {
    const orderDetails = extractCancellationOrderDetails(order);

    innerContent.push(
      <Row
        key={`cancelRow_${index}`}
        data-testid={`cancel-row-${order.orderId || index}`}
        gridGap="S400"
        className="hardware-orders-cancel-row"
      >
        <Col>
          <CancelOrderButton
            orderStatus={orderStatus}
            orderId={order.orderId || `order-${index}`}
            orderDetails={orderDetails}
            cancellationState={cancellationState}
            onConfirmCancellation={(reason) => onConfirmCancellation(order.orderId || "", reason)}
            orderItems={order.items as OrderItemWithHopDetails[] | undefined}
          />
        </Col>
      </Row>
    );
  }

  return innerContent;
};

export default MyHardwareOrdersTable;
